import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="composer-drop"
//
// Adds drag-and-drop file upload to the assistant composer. Files dropped
// anywhere on the controller's element (the form) get appended to the
// `images` file input via DataTransfer. The overlay only shows while a
// drag is over the form, and a small chip lists the queued filenames so
// the user knows their drop landed.
//
// Targets:
//   input    — the file input the dropped files merge into
//   overlay  — the "Drop files here" cue (toggled hidden)
//   queue    — small chip listing currently-queued filenames
export default class extends Controller {
  static targets = ["input", "overlay", "queue"]

  connect() {
    this.dragDepth = 0
    this.refreshQueue()
  }

  // Form-level dragenter/leave fire repeatedly as the cursor moves over
  // child elements; track depth so the overlay only hides on a true exit.
  dragenter(event) {
    if (!this.hasFiles(event)) return
    event.preventDefault()
    this.dragDepth++
    this.showOverlay()
  }

  dragover(event) {
    if (!this.hasFiles(event)) return
    // Required: without preventDefault the browser opens the file in a
    // new tab on drop instead of firing our drop handler.
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  dragleave(event) {
    if (!this.hasFiles(event)) return
    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.hideOverlay()
  }

  drop(event) {
    if (!this.hasFiles(event)) return
    event.preventDefault()
    this.dragDepth = 0
    this.hideOverlay()

    const dropped = Array.from(event.dataTransfer.files || [])
    if (dropped.length === 0) return
    this.addFiles(dropped)
  }

  // Bound to the file input's `change` event so manual picks also refresh
  // the queue display.
  inputChanged() {
    this.refreshQueue()
  }

  hasFiles(event) {
    return event.dataTransfer && Array.from(event.dataTransfer.types || []).includes("Files")
  }

  addFiles(newFiles) {
    const dt = new DataTransfer()
    if (this.inputTarget.files) {
      for (const f of this.inputTarget.files) dt.items.add(f)
    }
    for (const f of newFiles) dt.items.add(f)
    this.inputTarget.files = dt.files
    this.refreshQueue()
  }

  refreshQueue() {
    if (!this.hasQueueTarget) return
    const files = this.inputTarget.files
    if (!files || files.length === 0) {
      this.queueTarget.hidden = true
      this.queueTarget.textContent = ""
      return
    }
    const names = Array.from(files).map(f => f.name).join(", ")
    this.queueTarget.hidden = false
    this.queueTarget.textContent = `${files.length} file${files.length === 1 ? "" : "s"} queued: ${names}`
  }

  showOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.hidden = false
  }

  hideOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.hidden = true
  }
}
